import {
    DocumentExportFormat,
    EntityTypes,
    ExportOptions,
    NotePurposeTypesEnum,
    NoteStatusTypes,
    NoteTypes,
    StrategyTypes,
    SystemGeneratedDocumentsTypesEnum,
    UserRightsEnum,
} from '../../utilities/Constants';
import { DocumentSubTypeEnum, DocumentTypeEnum } from '../../utilities/DbConstants';
import { IExpertDetailModel, IExpertUpdateModel, IGeneralOpinion } from '../../interfaces/IExpert';
import { useGetExpert, useGetExpertKey } from './useGetExpert';
import { useLocation, useNavigate } from 'react-router-dom';

import { ApiRoutes } from '../../utilities/ApiRoutes';
import Authorization from '../../stores/Authorization';
import { CheckmateDialog } from '../../components/shared/dialog';
import { CheckmateEditIcon } from '../../shared/CheckmateIcons';
import { DisplayMessages } from '../../utilities/DisplayMessages';
import { DocumentTitle } from '../../components/shared/DocumentTitle';
import { DocumentsList } from '../../components/documents-list/DocumentsList';
import { ExpertDetailForm } from '../../components/expert/ExpertDetailForm';
import { ExportFileButton } from '../../components/shared/ExportFileButton';
import { IDocumentModel } from '../../interfaces/ICase';
import { INoteModel } from '../../interfaces/INote';
import { IValidation } from '../../interfaces/IError';
import { Loader } from '../../components/shared/Loader';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { MainContentOverlayComponent } from '../../components/shared/MainContentOverlay';
import { NoteEditor } from '../../components/notes/note-editor/NoteEditor';
import NoteHelper from '../../utilities/NoteHelper';
import { NoteListWrapper } from '../../components/case/static-sections/NoteListWrapper';
import { PageWithAffixedHeader } from '../../shared/PageWithAffixedHeader';
import Sort from '../../stores/Sort';
import { Stack } from 'react-bootstrap';
import UIHelper from '../../utilities/UIHelper';
import { cloneDeep } from 'lodash';
import { showErrorToast } from '../../shared/ToastUtils';
import { useAuthContext } from '../../contexts/AuthContext';
import { useExpertUpdateMutation } from './useExpertUpdateMutation';
import { useNoteCategoryTypes } from '../../shared/react-query-hooks/useNoteCategoryTypes';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useState } from 'react';
import { useZoneContext } from '../../contexts/useZoneContext';

export function ExpertDetail() {
    const { id: expertGuid } = useParams<{ id: string }>();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { state: locationState } = useLocation();
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();
    const [formData, setFormData] = useState<IExpertUpdateModel>();
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [openNoteEditor, setOpenNoteEditor] = useState<boolean>(false);
    const [forceNoteEditorInReadOnlyMode, setForceNoteEditorInReadOnlyMode] =
        useState<boolean>(false);
    const [currentNote, setCurrentNote] = useState<INoteModel>({
        status: { id: NoteStatusTypes.Open },
        purpose: { id: NotePurposeTypesEnum.Info },
    });
    const [validation, setValidation] = useState<IValidation>({});
    const [showDuplicateExpertMsg, setShowDuplicateExpertMsg] = useState<boolean>(false);

    const { isLoading, data: expert, error: apiError } = useGetExpert(expertGuid);
    const categoryNoteTypes = useNoteCategoryTypes(EntityTypes.AssertionDescriptorValueStrategy);

    const hasEditPermission = Authorization.userHasRight(
        UserRightsEnum.UpdateCaseExpert,
        user,
        zoneContext.zoneGuid
    );

    const hasViewExpertStrategyNotePermission = Authorization.userHasRight(
        UserRightsEnum.ViewExpertStrategyNote,
        user,
        zoneContext.zoneGuid
    );

    const updateMutation = useExpertUpdateMutation();

    const handleViewEditNote = (noteGuidToEdit: string, readOnly?: boolean) => {
        const validation: IValidation = {};

        const currentNote = expert?.notes?.find((x) => x.guid == noteGuidToEdit);

        if (!currentNote) {
            validation.model = [DisplayMessages.UnexpectedError];
            setValidation(validation);
            return;
        }
        setCurrentNote(JSON.parse(JSON.stringify(currentNote)));
        setOpenNoteEditor(true);
        setForceNoteEditorInReadOnlyMode(readOnly ?? false);
    };

    const handleClickAddNote = () => {
        const currentNote: INoteModel = {
            type: { id: NoteTypes.StrategyNote },
            status: { id: NoteStatusTypes.Open },
            parent: {
                entityType: { id: EntityTypes.Expert },
                entityGuid: expert?.guid,
            },
            purpose: { id: NotePurposeTypesEnum.Info },
            originStrategyType: StrategyTypes.Experts.Value,
        };

        setCurrentNote(currentNote);
        setOpenNoteEditor(true);
        setForceNoteEditorInReadOnlyMode(false);
    };

    const handleCancelNoteEditor = () => {
        setCurrentNote({
            status: { id: NoteStatusTypes.Open },
            purpose: { id: NotePurposeTypesEnum.Info },
        });
        setOpenNoteEditor(false);
    };

    const handleSaveClick = async () => {
        if (!expertGuid) return;

        const validation: IValidation = {};
        let hasErrors = false;

        if (!formData?.lastName) {
            validation.lastName = ['Required'];
            hasErrors = true;
        }

        if (!formData?.firstName) {
            validation.firstName = ['Required'];
            hasErrors = true;
        }

        if (!formData?.expertTypeId) {
            validation.type = ['Required'];
            hasErrors = true;
        }

        if (!formData?.disciplines?.length) {
            validation.disciplines = ['Required'];
            hasErrors = true;
        }

        setValidation(validation);

        if (hasErrors) {
            return;
        }

        const expertLastName = formData?.lastName ?? '';
        const response = await zoneContext.zoneApiClient.isExpertNameExists(
            expertLastName,
            expertGuid
        );

        if (response.errorMessage) {
            if (response.errors !== undefined && response.errors[0] !== undefined) {
                showErrorToast(response.errors[0].message ?? 'Failed to update Expert');
            }
            return;
        }

        if (response?.httpResponse.ok) {
            //if expert Last Name is duplicate then show the warning message.
            if (response.payload === true) {
                setShowDuplicateExpertMsg(true);
            } else {
                //other wise update Expert automatically.
                updateExpert();
            }
        }
    };

    const updateExpertWithDuplicateName = async () => {
        await updateExpert();
    };

    const updateExpert = async () => {
        const mappedFormData: IExpertUpdateModel = {
            ...formData,
            guid: expertGuid!,
            firstName: formData?.firstName ?? '',
            lastName: formData?.lastName ?? '',
            credentials: formData?.credentials ?? '',
            expertTypeId: formData?.expertTypeId ?? 0,
            disciplines: formData?.disciplines ?? [],
            generalOpinions: formData?.generalOpinions?.map(
                (go, idx) =>
                    ({
                        topic: {
                            ...go.topic,
                            correlationKeyId: idx,
                        },
                        opinion: {
                            ...go.opinion,
                            correlationKeyId: idx,
                        },
                    }) as IGeneralOpinion
            ),
        };

        updateMutation
            .mutateAsync(mappedFormData!)
            .then(({ payload }) => {
                if (payload) {
                    queryClient.setQueryData([useGetExpertKey, expert?.guid], {
                        ...formData, // this is a patch because the response isn't sending back disciplines. Only the payload.data should be needed here.
                        ...payload.data,
                    });
                }

                setIsEditMode(false);
            })
            .catch((error) => {
                showErrorToast(error.message ?? 'Failed to update Expert');
            });
    };

    const handleCancelClick = () => {
        const validation: IValidation = {};
        setValidation(validation);
        setIsEditMode(false);
    };

    const handleEditClick = () => {
        setIsEditMode(true);
    };

    const handleNoteSaveComplete = (noteItem: INoteModel) => {
        const expertCopy = { ...expert };
        const notesCopy = [...(expertCopy?.notes ?? [])];

        const foundIdx = notesCopy.findIndex((note) => note.guid === noteItem.guid) ?? -1;

        if (foundIdx > -1) {
            notesCopy[foundIdx] = noteItem;
        } else {
            notesCopy.push(noteItem);
        }

        notesCopy.sort(NoteHelper.sortNotesByStarredAndThenByModifiedDate);

        expertCopy.notes = notesCopy;

        queryClient.setQueryData([useGetExpertKey, expert?.guid], expertCopy);

        setOpenNoteEditor(false);
        setCurrentNote({
            status: { id: NoteStatusTypes.Open },
            purpose: { id: NotePurposeTypesEnum.Info },
        });
    };

    const handleDocumentSaveComplete = (updatedDocs: IDocumentModel[]) => {
        if (!expert) return;
        const docsCopy = cloneDeep(expert?.documents ?? []);

        for (const updatedDoc of updatedDocs) {
            const foundIdx = docsCopy.findIndex(
                (existingDoc) =>
                    existingDoc.guid?.trim().toLowerCase() === updatedDoc.guid?.trim().toLowerCase()
            );

            if (foundIdx > -1) {
                docsCopy.splice(foundIdx, 1, updatedDoc);
            } else {
                docsCopy.push(updatedDoc);
            }
        }

        queryClient.setQueryData<IExpertDetailModel | undefined>(
            [useGetExpertKey, expert?.guid],
            (previousExpert) => {
                if (!previousExpert) return previousExpert;

                const sortedDocs = docsCopy.sort(
                    Sort.compareDate('modifiedDate', undefined, 'desc')
                );

                return {
                    ...previousExpert,
                    documents: sortedDocs,
                };
            }
        );

        navigate({}, { replace: true }); // remove location state
    };

    const handleDocumentDeleteComplete = (documentGuid: string) => {
        const expertCopy = { ...expert };
        const docsCopy = [...(expertCopy?.documents ?? [])];

        const filteredDocsCopy = docsCopy.filter((doc) => doc.guid !== documentGuid);

        filteredDocsCopy.sort(Sort.compareDate('modifiedDate', undefined, 'desc'));

        expertCopy.documents = filteredDocsCopy;

        queryClient.setQueryData([useGetExpertKey, expert?.guid], expertCopy);
    };

    const showUpload = !!locationState?.showUpload;

    if (isLoading || updateMutation.isLoading) {
        return <Loader />;
    }

    if (apiError instanceof Error) {
        return (
            <div className="row mb-1 me-2">
                <span className="text-danger">{apiError.message}</span>
            </div>
        );
    }

    const pageTitle = `Expert | ${expert?.lastName}, ${expert?.firstName} `;
    return (
        <>
            <DocumentTitle title={pageTitle} />
            {openNoteEditor && (
                <MainContentOverlayComponent>
                    <NoteEditor
                        isReadonlyMode={forceNoteEditorInReadOnlyMode}
                        strategyTypeId={StrategyTypes.Experts.Value}
                        strategyName={expert?.firstName + ' ' + expert?.lastName}
                        noteCategoryTypes={categoryNoteTypes.data ?? []}
                        currentNote={currentNote}
                        onSaveComplete={handleNoteSaveComplete}
                        onCancel={handleCancelNoteEditor}
                    />
                </MainContentOverlayComponent>
            )}

            <PageWithAffixedHeader
                printHide={openNoteEditor}
                headerComponent={
                    <Stack direction="horizontal" style={{ justifyContent: 'space-between' }}>
                        <h1 className="mb-0">
                            Expert Profile - {UIHelper.formatExpertName(expert!)}
                        </h1>
                        <div style={{ paddingRight: '1em' }}>
                            {hasEditPermission && (
                                <Buttons
                                    expertGuid={expertGuid ?? ''}
                                    isEditMode={isEditMode}
                                    onSaveClick={handleSaveClick}
                                    onCancelClick={handleCancelClick}
                                    onEditClick={handleEditClick}
                                    onSetValidation={(validation) =>
                                        setValidation((prev) => ({ ...prev, ...validation }))
                                    }
                                />
                            )}
                            {hasViewExpertStrategyNotePermission && !isEditMode && (
                                <a className="btn btn-no-bg float-end text-gray" href="#Notes">
                                    <i className="fal fa-memo" />
                                    &nbsp;Strategy Notes
                                </a>
                            )}
                        </div>
                    </Stack>
                }
            >
                {validation?.model && <span className="text-danger">{validation.model}</span>}
                <ExpertDetailForm
                    user={user}
                    expert={expert}
                    onFormDataUpdate={setFormData}
                    isEditMode={isEditMode}
                    isCreateMode={false}
                    validation={validation}
                />
                <CheckmateDialog
                    isShowingModal={showDuplicateExpertMsg}
                    body="An expert with the same last name already exists. Do you want to continue?"
                    handleClose={() => {
                        setShowDuplicateExpertMsg(false);
                    }}
                    handleConfirm={() => {
                        updateExpertWithDuplicateName();
                        setShowDuplicateExpertMsg(false);
                    }}
                    confirmText="Continue"
                    cancelText="Cancel"
                    confirmButtonClassName="btn btn-black float-end"
                    dialogClassName="confirm-document-delete-dialog"
                    closeButtonClassName="btn btn-default float-end"
                />
                {!isEditMode && (
                    <>
                        <div className="row" id="AttachmentsDiv">
                            <DocumentsList
                                authorizedToEdit={Authorization.userHasRight(
                                    UserRightsEnum.UpdateExpert,
                                    user,
                                    zoneContext.zoneGuid
                                )}
                                displayUploadDocumentDialog={showUpload}
                                documents={expert?.documents}
                                documentSubTypeId={DocumentSubTypeEnum.Expert}
                                documentTypeId={DocumentTypeEnum.Strategy}
                                entityGuid={expertGuid!}
                                entityTypeId={EntityTypes.Expert}
                                onDeleteComplete={handleDocumentDeleteComplete}
                                onSaveComplete={handleDocumentSaveComplete}
                                parentEntityJson={JSON.stringify(expert)}
                                parentEntityRedirectUrl={LocalRoutes.ExpertDetail.replace(
                                    ':id',
                                    expertGuid!
                                )}
                                title="Attachments"
                                uploadOnly
                                user={user}
                            />
                        </div>
                        <div className="row" id="Notes" style={{ marginTop: '15px' }}>
                            <NoteListWrapper
                                title={'Strategy Notes'}
                                titleClassName="text-gray font-size-sm"
                                entityType={EntityTypes.Expert}
                                user={user}
                                notes={expert?.notes ?? []}
                                onViewEditNote={handleViewEditNote}
                                onAddNote={handleClickAddNote}
                                noteType={NoteTypes.StrategyNote}
                                strategyType={StrategyTypes.Experts.Value}
                            />
                        </div>
                    </>
                )}
            </PageWithAffixedHeader>
        </>
    );
}

interface IButtonsProps {
    expertGuid: string;
    isEditMode: boolean;
    onEditClick: () => void;
    onCancelClick: () => void;
    onSaveClick: () => void;
    onSetValidation: (validation: IValidation) => void;
}

function Buttons({
    expertGuid,
    isEditMode,
    onEditClick,
    onCancelClick,
    onSaveClick,
    onSetValidation,
}: IButtonsProps) {
    const handleExportError = () => {
        onSetValidation({ model: [DisplayMessages.UnexpectedError] });
    };
    if (isEditMode) {
        return (
            <div>
                <button type="button" className="btn btn-orange me-2" onClick={onSaveClick}>
                    Save
                </button>
                <button type="button" className="btn btn-default" onClick={onCancelClick}>
                    Cancel
                </button>
            </div>
        );
    }

    return (
        <span>
            <ExportFileButton
                icon="fa-file-pdf"
                onError={handleExportError}
                reportParameters={{
                    reportType: {
                        id: SystemGeneratedDocumentsTypesEnum.ExpertStrategyDetailExport.Value,
                    },
                    options: { key: expertGuid },
                }}
                reportType={{
                    id: SystemGeneratedDocumentsTypesEnum.ExpertStrategyDetailExport.Value,
                    availableReportOptions: {
                        customOptions: [
                            ExportOptions.IncludeTactics,
                            ExportOptions.IncludeAttachments,
                            ExportOptions.IncludeStrategyNotes,
                        ],
                    },
                }}
                exportFormat={DocumentExportFormat.PDF}
                url={'/' + ApiRoutes.ExportReport}
            />
            <button className="btn btn-no-bg float-end text-gray" onClick={onEditClick}>
                <CheckmateEditIcon />
                &nbsp;Edit
            </button>
        </span>
    );
}
